/** @format */

import {
  Chain,
  MultipleProductsByEcodeParams,
  MultipleProductsByEcodeResponse,
  ProcessedProduct,
  ProductDetailsByUrlResponse,
  ProductSku,
  ProductsData,
  PromotionDetailsMapV1,
  PromotionDetailsMapV2,
  SkuEDDResponse,
  StyleDefiningAttribute,
  StyleDescriptiveAttribute,
  ProductStylePrices,
} from '@shared/data-access/interfaces';
import { HttpService, UtilityService } from '@shared/data-access/services';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';

import { isPlatformServer } from '@angular/common';
import { Observable } from 'rxjs';
import { ProductDao } from './product.dao';
import { TimeService } from '@shared/utils/services';
import { map } from 'rxjs/operators';
import { plaCheck } from '@shared/data-access/constants';
import { SwatchColorSanitizerService } from '@shared/media/data-access';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly productDao = inject(ProductDao);
  private readonly chain = inject(Chain);
  private readonly state = inject(TransferState);
  private readonly utilityService = inject(UtilityService);
  private readonly timeService = inject(TimeService);
  private readonly httpService = inject(HttpService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly swatchColorSanitizerService = inject(SwatchColorSanitizerService);

  readonly PRODUCT_BY_ID_STATE_KEY_PREFIX = 'product-';

  /**
   * @description
   * Accepts an array of descriptive attributes and returns a list of displayable ones if they exist, otherwise an empty
   * array is returned.
   */
  getDisplayableDescriptiveAttributesQV(
    descriptiveAttributes: StyleDescriptiveAttribute[],
  ): StyleDescriptiveAttribute[] {
    // Displayable attributes
    const displayableAttributes: StyleDescriptiveAttribute[] = [];

    // Ensure there are attributes to iterate
    if (Array.isArray(descriptiveAttributes) && descriptiveAttributes.length > 0) {
      // Iterate the attributes
      for (const attribute of descriptiveAttributes) {
        // Check to see if the attribute is displayable
        if (this.utilityService.isTruthy(attribute?.isDisplayable)) {
          // check if dsg pro tips is in the array and pro tips flag is true
          if (attribute.name.toLowerCase() !== 'dsg pro tips' || this.chain.proTipsFlag) {
            // Push the attribute to the return array
            displayableAttributes.push(attribute);
          }
        }
      }
    }

    // Return the displayable attributes
    return displayableAttributes;
  }

  /**
   * @description
   * Returns Promotion details for a given Product ID
   */
  public getPromotionsByProductId$(
    catEntryId: string | number,
    channelId: string,
    skuIdList?: string | string[],
    useNewPricingService?: boolean,
    retries?: number,
  ): Observable<PromotionDetailsMapV1 | PromotionDetailsMapV2> {
    return this.productDao.getPromotionsByProductId$(
      catEntryId,
      channelId,
      skuIdList,
      useNewPricingService,
      retries,
    );
  }

  /**
   * @returns True or False if product is coming soon
   *
   * @description
   * Checks to see if product is coming soon
   *
   */
  public isProductComingSoon(stringDate: string): boolean {
    if (!stringDate) {
      return false;
    }

    const currentDate = this.timeService.getCurrentTime();

    const comingSoonDate = this.timeService.parseDateFromMDY(stringDate);
    return Boolean(comingSoonDate > currentDate);
  }

  public setProductInStockReminder(
    userEmail: string,
    skuId: string,
    productTitle: string,
    productURL: string,
    imageURL: string,
    attributes: { label: string; value: string }[],
    storeNumber: string,
    storeName?: string,
    retries = 0,
  ): Observable<unknown> {
    return this.productDao.setProductInStockReminder$(
      userEmail,
      skuId,
      productTitle,
      productURL,
      imageURL,
      attributes,
      storeNumber,
      storeName,
      retries,
    );
  }

  /**
   * Checks whether the current PDP visit is from a PLA campaign. Works on the client or server side.
   */
  public isPLA(ignoreReferrer = false): boolean {
    const referrer = this.httpService.getReferrer();
    const queryString = this.httpService.getQueryString();
    const campParam = this.httpService.getParameterValuesFromQueryString(queryString, 'camp');
    const emailPLAParam = this.httpService.getParameterValuesFromQueryString(
      queryString,
      'EmailPLA',
    );

    return plaCheck(referrer, campParam, emailPLAParam, ignoreReferrer);
  }

  /**
   * Reuses the server's PLA decision during hydration
   */
  isPLAWithTransferState() {
    const stateKey = makeStateKey<boolean>('is-pla');

    if (this.state.hasKey(stateKey)) {
      return this.state.get(stateKey, false);
    }

    const isPLA = this.isPLA();
    if (isPlatformServer(this.platformId)) {
      this.state.set(stateKey, isPLA);
    }

    return isPLA;
  }

  /**
   * @description
   * Will return defaultColor if product is defined
   * @param product
   */
  public determineProductDefaultColor(product: ProcessedProduct): string | undefined {
    if (product !== undefined && product !== null) {
      // Default the selected color attribute
      return product.defaultColor;
    }
    return undefined;
  }

  public getColorForSkuId(skus: ProductSku[], skuId: string): string | undefined {
    const sku = skus?.find((s) => s.partNumber === skuId);
    if (!sku) {
      return undefined;
    }

    const colorAttribute = sku.definingAttributes?.find(
      (attr) => typeof attr.name === 'string' && attr.name.trim().toLowerCase() === 'color',
    );

    return colorAttribute?.value;
  }

  getInitialSelectedColor(
    definingAttributes: StyleDefiningAttribute[] | undefined,
    skus: ProductSku[] | null | undefined,
    colorQueryParam?: string,
    skuIdQueryParam?: string,
  ): string | undefined {
    if (!Array.isArray(definingAttributes)) {
      return undefined;
    }

    const colorAttribute = definingAttributes.find(
      (attribute) => attribute?.name?.trim().toLowerCase() === 'color',
    );

    if (!colorAttribute?.value) {
      return undefined;
    }

    const colors = colorAttribute?.value?.split('|') ?? [];

    const colorFromSku = this.getColorForSkuId(skus ?? [], skuIdQueryParam ?? '');
    const selectedSkuColor = this.findMatchingColor(colorFromSku, colors);

    if (selectedSkuColor) {
      return selectedSkuColor; // Sku param takes precedence over color param
    }

    const normalizedQueryColor = this.swatchColorSanitizerService.sanitizeEncoded(colorQueryParam);
    const selectedQueryColor = this.findMatchingColor(normalizedQueryColor, colors);

    return selectedQueryColor ?? colors[0];
  }

  private findMatchingColor(
    color: string | undefined,
    availableColors: string[],
  ): string | undefined {
    if (!color) {
      return undefined;
    }

    const normalizedColor = this.swatchColorSanitizerService.sanitize(color);
    return availableColors.find(
      (availableColor) =>
        this.swatchColorSanitizerService.sanitize(availableColor) === normalizedColor,
    );
  }

  /** Quick View Logic */
  public getDefaultColorFromDefiningAttribute(defAttribute: StyleDefiningAttribute): string {
    if (defAttribute.name.trim().toLowerCase() === 'color') {
      if (defAttribute.value.includes('|')) {
        // Get the first color
        return defAttribute.value.substring(0, defAttribute.value.indexOf('|'));
      } else {
        // Set the only available color
        return defAttribute.value;
      }
    }
  }

  /**
   * @description
   * Gets the Page object by an url and type.  Types: `category`, `product`, `name`.  The returned Page object will
   * contain all product data related information. both ECODE and SKU level
   */
  public getProductByUrl$(
    url: string,
    partNumber: string,
    channel: string,
    clearanceFlag?: string,
    antiClearanceFlag?: string,
    categoryId?: string,
    productParamType?: string,
    retries?: number,
  ): Observable<ProductDetailsByUrlResponse> {
    const STATE_KEY = makeStateKey<ProductDetailsByUrlResponse>(
      `product-url-${this.PRODUCT_BY_ID_STATE_KEY_PREFIX}${url}-${categoryId || ''}-${
        clearanceFlag || ''
      }`,
    );

    // Attempt to get the state by key
    const stateData = this.state.get(STATE_KEY, null);
    // Check to see if state exists
    if (stateData) {
      // Return the existing state
      return new Observable((observer) => {
        observer.next(stateData);
        observer.complete();
      });
    }

    return this.productDao
      .getProductByUrl$(
        url,
        partNumber,
        channel,
        clearanceFlag,
        antiClearanceFlag,
        categoryId,
        productParamType,
        retries,
      )
      .pipe(
        map((tempProduct: ProductDetailsByUrlResponse) => {
          const product: ProductDetailsByUrlResponse =
            tempProduct === null ? undefined : Object.assign(tempProduct);
          // Set the state
          this.state.set<ProductDetailsByUrlResponse>(STATE_KEY, structuredClone(product));
          return product;
        }),
      );
  }

  public getProductByEcodeV4$(productId: string, checkClearance = false, retries = 0) {
    return this.productDao
      .getProductByEcodeV4$(productId, checkClearance, retries)
      .pipe(map((tempProductResponse) => tempProductResponse?.productsData[0]));
  }

  public static generateImageUrlFromAttributes(productDetails: ProductsData, extraParams = '') {
    const base = `https://dks.scene7.com/is/image/dkscdn/${productDetails.style.partNumber}`;

    const attributes = productDetails.style.definingAttributes;

    const imageParams = `?$UTPMain$${extraParams}`;

    const suffix = `is${imageParams}`;

    for (const currentAttribute of attributes) {
      if (currentAttribute.name.toLowerCase() === 'color') {
        const colorArray = currentAttribute.value.split('|');
        let sanitizedColor = colorArray[0].replaceAll('/', '_');
        sanitizedColor = sanitizedColor.replaceAll(' ', '_');
        return `${base}_${sanitizedColor}_${suffix}`;
      }
    }

    return `${base}${imageParams}`;
  }

  public static generateDisplayPrice(price: ProductStylePrices) {
    return {
      isSeePriceInCart: price.mapPriceIndicator === 1,
      isRangePrice: price.minofferprice !== price.maxofferprice,
      lowestPrice: price.minofferprice,
      highestPrice: price.maxofferprice,
    };
  }

  /**
   * @description
   * Returns Sku EDD
   */
  public getSkuDeliveryDate$(skuId: string): Observable<SkuEDDResponse> {
    return this.productDao.getSkuDeliveryDate$(skuId);
  }

  public getMultipleProductsByEcode$<
    T extends MultipleProductsByEcodeParams | undefined = undefined,
  >(ecodes: string[], options?: T, retries = 0): Observable<MultipleProductsByEcodeResponse<T>> {
    return this.productDao.getMultipleProductsByEcode$(ecodes, options, retries);
  }

  public getProductsBySkus$(skus: string[], retries = 0) {
    return this.productDao.getProductsBySkus$(skus, retries);
  }
}
